import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { useResponsive } from '@/hooks/useResponsive';
import RevealText from '@/v2/components/RevealText';

interface FAQItem {
  questionKey: string;
  answerKey: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    questionKey: 'faq.questions.whatIs.question',
    answerKey: 'faq.questions.whatIs.answer',
  },
  {
    questionKey: 'faq.questions.getStarted.question',
    answerKey: 'faq.questions.getStarted.answer',
  },
  {
    questionKey: 'faq.questions.languages.question',
    answerKey: 'faq.questions.languages.answer',
  },
  {
    questionKey: 'faq.questions.tasks.question',
    answerKey: 'faq.questions.tasks.answer',
  },
  {
    questionKey: 'faq.questions.feedback.question',
    answerKey: 'faq.questions.feedback.answer',
  },
];

interface FAQItemComponentProps {
  questionKey: string;
  answerKey: string;
  isOpen: boolean;
  onClick: () => void;
  isFirst?: boolean;
}

const FAQItemComponent = ({
  questionKey,
  answerKey,
  isOpen,
  onClick,
  isFirst = false,
}: FAQItemComponentProps) => {
  const { t } = useTranslation();

  return (
    <div className="border-border-split border-b">
      <button
        className={`flex w-full items-center  justify-between ${isFirst ? 'pt-[6px]' : 'pt-8'} ${isOpen ? 'pb-5' : 'can-hover:hover:opacity-60 pb-8'} cursor-pointer border-none bg-transparent text-left outline-none transition-[padding] duration-300 focus:outline-none`}
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <span className="text-content-primary text-xl font-medium leading-[1.2]">
          {t(questionKey)}
        </span>
        <div
          className={clsx('text-content-primary transition-transform duration-300', {
            'rotate-45': isOpen,
          })}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14.595731735229492"
            height="14.595731735229492"
            viewBox="0 0 14.595731735229492 14.595731735229492"
          >
            <path
              d="M13.8659 6.56802 L8.02771 6.56802 L8.02771 0.729844 C8.02771 0.32695 7.70076 0 7.29787 0 C6.89497 0 6.56802 0.32695 6.56802 0.729844 L6.56802 6.56802 L0.729844 6.56802 C0.32695 6.56802 0 6.89497 0 7.29787 C0 7.70076 0.32695 8.02771 0.729844 8.02771 L6.56802 8.02771 L6.56802 13.8659 C6.56802 14.2696 6.89497 14.5957 7.29787 14.5957 C7.70076 14.5957 8.02771 14.2696 8.02771 13.8659 L8.02771 8.02771 L13.8659 8.02771 C14.2696 8.02771 14.5957 7.70076 14.5957 7.29787 C14.5957 6.89497 14.2696 6.56802 13.8659 6.56802 Z"
              fill="currentColor"
              fillRule="nonzero"
            />
          </svg>
        </div>
      </button>
      {/* 使用 CSS Grid 实现平滑高度动画 */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{
          gridTemplateRows: isOpen ? '1fr' : '0fr',
        }}
        aria-hidden={!isOpen}
      >
        <div className="overflow-hidden">
          <p className="text-content-primary m-0 pb-[39px] text-sm leading-normal opacity-80">
            {t(answerKey)}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * 第四屏组件 - FAQ
 */
export const FourthScreen = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number>(0);
  const { isMobile } = useResponsive();

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  if (isMobile) {
    return (
      <div className="bg-background-primary box-border flex items-center justify-center px-[28px] lg:px-0">
        <div className="flex w-full flex-col gap-[58px] lg:flex-row">
          {/* 左侧标题 */}
          <div className="w-full shrink-0 text-[32px] font-semibold leading-[1.2]">
            <h2 className="text-content-primary m-0">
              <RevealText text={t('faq.title.line1')} steps={5} interval={100} animation="fade" />
              <br />
              <RevealText
                text={t('faq.title.line2')}
                steps={5}
                interval={100}
                delay={300}
                animation="fade"
              />
            </h2>
          </div>

          {/* 右侧 FAQ 列表 */}
          <div className="flex-1">
            <div className="flex flex-col">
              {FAQ_ITEMS.map((item, index) => (
                <FAQItemComponent
                  key={index}
                  questionKey={item.questionKey}
                  answerKey={item.answerKey}
                  isOpen={openIndex === index}
                  onClick={() => handleToggle(index)}
                  isFirst={index === 0}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className=" bg-background-primary box-border flex items-center justify-center px-[28px] md:px-[120px] lg:px-0">
      <div className="flex min-h-[560px] w-full max-w-screen-lg flex-col gap-10 lg:flex-row lg:gap-[143px]">
        {/* 左侧标题 */}
        <div className="w-full shrink-0 text-[36px] leading-[1.2] md:text-[48px] lg:w-[405px]">
          <h2 className="text-content-primary m-0 font-medium leading-[1.2]">
            <RevealText text={t('faq.title.line1')} steps={5} interval={100} animation="fade" />
            <br />
            <RevealText
              text={t('faq.title.line2')}
              steps={5}
              interval={100}
              delay={300}
              animation="fade"
            />
          </h2>
        </div>

        {/* 右侧 FAQ 列表 */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-col">
            {FAQ_ITEMS.map((item, index) => (
              <FAQItemComponent
                key={index}
                questionKey={item.questionKey}
                answerKey={item.answerKey}
                isOpen={openIndex === index}
                onClick={() => handleToggle(index)}
                isFirst={index === 0}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FourthScreen;
